<template>
  <CustomModal ref="modal" title="Paiements reçus">
    <div class="payments-modal" v-if="association">

      <div class="document">
        <div>
          <div class="label">Document</div>
          <div class="document-name">{{ documentLabel }}</div>
        </div>
        <div class="document-status">
          <div class="label">Statut sur la plateforme</div>
          <span class="transmission-status" :title="getTransmissionStatus(currentStatus)" :style="getTransmissionStatusStyle(currentStatus)">
            {{ getTransmissionStatus(currentStatus) }}
          </span>
        </div>
      </div>

      <TableScopeNotice>
        Chaque paiement reçu est déclaré à la plateforme (statut « Encaissée », par taux de TVA) uniquement lorsque vous cliquez sur « Transmettre ». Un paiement déjà « Transmis » ne peut plus être déclaré.
      </TableScopeNotice>

      <p v-if="isLoading && !view" class="state-message">Chargement des paiements…</p>
      <p v-else-if="!view" class="state-message state-error">Les paiements n'ont pas pu être chargés.</p>
      <p v-else-if="!view.declarable" class="state-message">{{ view.reason }}</p>

      <template v-else>
        <p v-for="notice in view.notices" :key="notice.code" class="notice" :style="{color: severityColor(notice.severity)}">{{ notice.message }}</p>

        <p v-if="payments.length === 0" class="state-message">Aucun paiement reçu sur ce document.</p>

        <template v-else>
          <div class="totals">
            <div><span class="label">Encaissé</span><span class="amount">{{ formatEuro(view.summary.collected) }}</span></div>
            <div><span class="label">Transmis</span><span class="amount">{{ formatEuro(view.summary.declared) }}</span></div>
            <div><span class="label">À transmettre</span><span class="amount">{{ formatEuro(view.summary.toDeclare) }}</span></div>
          </div>

          <ul class="payment-list">
            <li v-for="payment in payments" :key="payment.collectionId" class="payment">
              <div class="payment-main">
                <span class="payment-amount">{{ formatEuro(payment.amount) }}</span>
                <span class="payment-date">{{ formatDay(payment.date) }}<template v-if="paymentTypeLabel(payment.paymentType)"> · {{ paymentTypeLabel(payment.paymentType) }}</template></span>
                <span class="payment-badge" :style="paymentStatusStyle(payment.status)">{{ paymentStatusLabel(payment.status) }}</span>
              </div>

              <div v-if="payment.vatLines.length" class="payment-vat" :title="vatBreakdown(payment)">{{ vatBreakdown(payment) }}</div>

              <p v-if="payment.sentAt" class="payment-line">
                Transmis le {{ formatDateTime(payment.sentAt) }}<template v-if="payment.forced"> (déclaré à votre demande)</template>
              </p>
              <p v-if="payment.reason" class="payment-line">{{ payment.reason }}</p>
              <p v-if="payment.error" class="payment-line payment-error">
                {{ payment.error.message }}<template v-if="payment.error.status"> (code {{ payment.error.status }})</template>
                <span class="payment-attempt" v-if="payment.lastAttemptAt">Dernier essai le {{ formatDateTime(payment.lastAttemptAt) }}.</span>
              </p>

              <div v-if="confirmForceId === payment.collectionId" class="force-confirm">
                <p>Ce paiement n'a pas à être déclaré. Le transmettre quand même ?</p>
                <div class="payment-actions">
                  <button type="button" class="inline-action" :disabled="!!busy" @click="confirmForceId = undefined">Annuler</button>
                  <button type="button" class="inline-action primary" :disabled="!!busy" @click="declare(payment.collectionId, true)">
                    {{ busy === payment.collectionId ? 'Transmission…' : 'Transmettre quand même' }}
                  </button>
                </div>
              </div>
              <div v-else-if="payment.canSend || payment.canForce" class="payment-actions">
                <button v-if="payment.canSend" type="button" class="inline-action primary" :disabled="!!busy" @click="declare(payment.collectionId, false)">
                  {{ busy === payment.collectionId ? 'Transmission…' : (payment.status === 'error' ? 'Réessayer' : 'Transmettre') }}
                </button>
                <button v-else type="button" class="inline-action" :disabled="!!busy" @click="confirmForceId = payment.collectionId">Déclarer quand même</button>
              </div>
            </li>
          </ul>
        </template>
      </template>

      <div class="buttons-container">
        <CustomButton @click="closeModal" :params="closeButton" :isSecondary="true"/>
        <CustomButton v-if="sendableCount > 1" @click="declare(undefined, false)" :params="sendAllButton"/>
      </div>

    </div>
  </CustomModal>
</template>

<script>
import CustomModal from "@/components/modals/CustomModal.vue";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import TableScopeNotice from "@/components/TableScopeNotice.vue";
import {NotificationService} from "@vues/v-common";
import {PaymentTransmissionController} from "@/invoice-transmission/controllers/invoice_transmission/PaymentTransmissionController";
import {getTransmissionStatus, getTransmissionStatusStyle} from "@/invoice-transmission/utils";
import {E_REPORTING_SEVERITY, formatDay, formatEuro} from "@/invoice-transmission/eReportingUtils";
import {
  formatDateTime,
  paymentStatusLabel,
  paymentStatusStyle,
  paymentTypeLabel,
  vatBreakdown
} from "@/invoice-transmission/paymentTransmissionUtils";

/**
 * Paiements reçus sur une facture (ou un acompte) transmise à la plateforme : état de leur déclaration ("Encaissée"), transmission
 * paiement par paiement ou en une fois, nouvel essai après une erreur. Ouvrir la fenêtre ne transmet rien.
 */
export default {
  name: "InvoiceTransmissionPaymentsModal",
  components: {CustomModal, CustomButton, TableScopeNotice},
  // Rechargement du tableau des factures, appelé après une transmission
  props: {
    callback: {type: Function, default: undefined}
  },
  data() {
    return {
      association: undefined,
      documentLabel: "",
      view: undefined,
      isLoading: false,
      // Paiement en cours de transmission ("all" : tous)
      busy: undefined,
      // Paiement dont la transmission forcée attend une confirmation
      confirmForceId: undefined,
      closeButton: {text: "Fermer"}
    };
  },
  computed: {
    currentStatus() {
      return this.view?.invoiceStatus ?? this.association?.providers?.superpdp?.status;
    },
    payments() {
      return this.view?.payments ?? [];
    },
    sendableCount() {
      return this.payments.filter(payment => payment.canSend).length;
    },
    sendAllButton() {
      return {text: `Tout transmettre (${this.sendableCount})`, loading: this.busy === "all", disabled: !!this.busy};
    }
  },
  methods: {
    getTransmissionStatus,
    getTransmissionStatusStyle,
    formatEuro,
    formatDay,
    formatDateTime,
    paymentStatusLabel,
    paymentStatusStyle,
    paymentTypeLabel,
    vatBreakdown,
    severityColor(severity) {
      return (E_REPORTING_SEVERITY[severity] || E_REPORTING_SEVERITY.info).color;
    },
    async openModal(association, documentLabel) {
      this.association = association;
      this.documentLabel = documentLabel;
      this.view = undefined;
      this.busy = undefined;
      this.confirmForceId = undefined;

      this.$refs.modal.openModal();

      await this.load();
    },
    closeModal() {
      this.$refs.modal.closeModal();
    },
    async load() {
      this.isLoading = true;

      const resp = await PaymentTransmissionController.getPayments(this.association._id);
      if (resp && !resp.errors) this.view = resp;

      this.isLoading = false;
    },
    /**
     * Transmet un paiement (collectionId) ou tous ceux à transmettre. Une erreur de la plateforme n'est pas une erreur de l'appel : elle
     * est enregistrée sur le paiement, qui reste à réessayer.
     */
    async declare(collectionId, force) {
      if (this.busy) return;
      this.busy = collectionId ?? "all";

      const sentBefore = this.payments.filter(payment => payment.status === "sent").length;
      const resp = await PaymentTransmissionController.declare(this.association._id, collectionId, force);

      this.busy = undefined;
      this.confirmForceId = undefined;

      if (resp && !resp.errors) {
        this.view = resp;
        this.notifyResult(resp.payments.filter(payment => payment.status === "sent").length - sentBefore, resp.payments.filter(payment => payment.status === "error").length);
      } else {
        // Refusé (déjà transmis, plus à déclarer...) : on remontre l'état réel
        await this.load();
      }

      if (this.callback) await this.callback();
    },
    notifyResult(sent, errors) {
      const notification = new NotificationService();
      if (sent > 0) notification.addNotification("success", sent === 1 ? "Paiement transmis à la plateforme" : `${sent} paiements transmis à la plateforme`);
      if (errors > 0) notification.addNotification("danger", "La plateforme a refusé un paiement : le détail est affiché sous le paiement concerné");
    }
  }
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl'

.payments-modal
  display flex
  flex-direction column
  padding 10px 0 0

.label
  font-size 11px
  color #666
  margin-bottom 4px

.document
  display flex
  justify-content space-between
  align-items flex-start
  gap 15px
  margin-bottom 15px

  > div
    min-width 0

.document-name
  font-weight bold
  color FONT_COLOR

.document-status
  display flex
  flex-direction column
  align-items flex-end

.transmission-status
  display inline-block
  max-width 100%
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  box-sizing border-box
  padding 4px 10px
  border-radius 20px
  font-size 12px
  font-weight bold

.state-message
  margin 6px 0 14px
  font-size 13px
  color #4a5a7a

  &.state-error
    color #a61b1b

.notice
  margin 0 0 8px
  font-size 12px

.totals
  display flex
  justify-content space-between
  gap 10px
  margin-bottom 12px

  > div
    display flex
    flex-direction column
    min-width 0

  .amount
    font-weight bold
    font-size 14px
    color FONT_COLOR

.payment-list
  list-style none
  margin 0
  padding 0
  display flex
  flex-direction column
  gap 8px

.payment
  box-sizing border-box
  padding 10px 12px
  border 1px solid #b9d3f5
  border-radius 8px
  background white

.payment-main
  display flex
  align-items center
  gap 8px

.payment-amount
  flex none
  font-weight bold
  font-size 15px
  color FONT_COLOR

.payment-date
  flex 1
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-size 12px
  color #4a5a7a

.payment-badge
  flex none
  padding 3px 9px
  border-radius 20px
  font-size 11px
  font-weight bold
  white-space nowrap

.payment-vat
  margin-top 4px
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-size 12px
  color #4a5a7a

.payment-line
  margin 6px 0 0
  font-size 12px
  line-height 1.4
  color #4a5a7a

  &.payment-error
    color #a61b1b

.payment-attempt
  display block
  margin-top 2px
  color #4a5a7a

.force-confirm
  margin-top 8px
  padding 8px 10px
  border 1px solid #b9d3f5
  border-left 4px solid #0069de
  border-radius 6px
  background #eaf2fd

  p
    margin 0 0 8px
    font-size 12px
    color FONT_COLOR

.payment-actions
  display flex
  justify-content flex-end
  gap 8px
  margin-top 8px

.inline-action
  min-height 30px
  padding 4px 12px
  border 1px solid #0069de
  border-radius 6px
  background white
  color #0069de
  font-size 12px
  font-weight bold
  cursor pointer

  &.primary
    background #0069de
    color white

  &:disabled
    opacity .6
    cursor default

.buttons-container
  display flex
  gap 15px
  margin-top 18px
</style>
